'use client'

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { DialogProps } from '@radix-ui/react-dialog'
import { sendGForm } from '@/lib/utils'
import { useUserContext } from '@/lib/contexts/UserContext'

const emojis = ['😞', '😕', '😐', '🙂', '😄']

const VIEW_RATING = 'VIEW_RATING'
const VIEW_FEEDBACK = 'VIEW_FEEDBACK'
const VIEW_THANK_YOU = 'VIEW_THANK_YOU'

export type FeedbackDialogProps = DialogProps

export function FeedbackDialog({ open, onOpenChange, ...props }: FeedbackDialogProps) {
  const [view, setView] = useState(VIEW_RATING)
  const [loading, setLoading] = useState(false)
  const [rating, setRating] = useState<number | null>(null)
  const [feedback, setFeedback] = useState('')
  const { user } = useUserContext()

  const handleRatingSelect = (index: number) => {
    setRating(index + 1)
    setView(VIEW_FEEDBACK)
  }

  const handleFeedbackSubmit = async () => {
    setLoading(true)

    try {
      await sendGForm({
        username: user?.username ?? '',
        email: user?.email ?? '',
        plan: user?.plan?.name ?? user?.team?.subscription?.plan?.name ?? '',
        score: rating ? `${rating}` : '',
        feedback: feedback ?? '',
      })
    } catch (e) {
      console.error(e)
    }

    setRating(null)
    setFeedback('')
    setLoading(false)
    setView(VIEW_THANK_YOU)
  }

  const handleClose = () => {
    if (onOpenChange) {
      onOpenChange(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} {...props}>
      <DialogContent className="!tw-max-w-sm" onOpenAutoFocus={(e) => e.preventDefault()}>
        {view === VIEW_RATING && (
          <>
            <DialogHeader>
              <DialogTitle>Hey there!</DialogTitle>
              <DialogDescription>How do you feel about this new website design?</DialogDescription>
            </DialogHeader>
            <div className="py-4 tw-flex tw-justify-center tw-space-x-2">
              {emojis.map((emoji, index) => (
                <div
                  className="tw-relative tw-flex tw-h-12 tw-flex-1 tw-items-center tw-justify-center tw-transition-transform hover:!tw-scale-110"
                  key={index}
                >
                  <Button
                    variant="transparent"
                    size="icon"
                    className={`tw-absolute !tw-text-4xl`}
                    onClick={() => handleRatingSelect(index)}
                  >
                    {emoji}
                    <span className="sr-only">Rate {index + 1} out of 5</span>
                  </Button>
                </div>
              ))}
            </div>
          </>
        )}
        {view === VIEW_FEEDBACK && (
          <>
            <DialogHeader>
              <DialogTitle>Thanks for your rating!</DialogTitle>
              <DialogDescription>
                We&#39;d love to hear more about your experience. What did you like, and what can we improve?
              </DialogDescription>
            </DialogHeader>
            <textarea
              className="tw-mb-4 tw-h-32 tw-w-full tw-resize-none tw-rounded-md tw-border tw-bg-primary tw-p-2"
              placeholder="Your feedback..."
              rows={3}
              autoFocus
              onChange={(e) => setFeedback(e.target.value)}
            />
            <DialogFooter className="tw-justify-between">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setView(VIEW_RATING)}
                disabled={loading}
                className="tw-w-full"
              >
                Back
              </Button>
              <Button type="button" onClick={handleFeedbackSubmit} disabled={loading} className="tw-w-full">
                Submit
              </Button>
            </DialogFooter>
          </>
        )}
        {view === VIEW_THANK_YOU && (
          <>
            <DialogHeader>
              <DialogTitle>Thank you for your feedback!</DialogTitle>
              <DialogDescription>We appreciate you taking the time to help us improve.</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button type="button" onClick={handleClose} className="tw-w-full">
                Close
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
